"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Cộng đồng theo từng lab: nút nổi "💬 Thảo luận" → drawer.
 * Tự nhận lab id từ URL (/lab/<id>); ẩn ở hub, trang môn, trang khác.
 * Tạo topic / reply / upvote–downvote; tối thiểu 50 từ; có gợi ý format.
 */

const MIN_WORDS = 50;

type Topic = { id: string; kind: string; title: string; body: string; author: string; createdAt: number; score: number; myVote: number; replyCount: number };
type Reply = { id: string; topicId: string; body: string; author: string; createdAt: number };

const FORMATS: Array<{ kind: string; label: string; hint: string }> = [
  { kind: "suggest", label: "💡 Đề nghị bổ sung", hint: "Mình đề nghị bổ sung … vào lab này, vì … Điều đó sẽ giúp người học …" },
  { kind: "issue", label: "🔧 Chưa ổn, cần sửa", hint: "Chỗ này mình thấy chưa ổn: … Mình nghĩ nên sửa/nâng cấp thành … bởi vì …" },
  { kind: "question", label: "❓ Đặt câu hỏi", hint: "Mình chưa hiểu phần … Cụ thể là … Có ai giải thích giúp mình được không?" },
  { kind: "new-lab", label: "✨ Đề xuất lab mới", hint: "Mình muốn có một lab về … để hiểu … tốt hơn. Lab đó nên có … và giúp người học …" },
];
const KIND_LABEL: Record<string, string> = { suggest: "💡 Đề nghị", issue: "🔧 Cần sửa", question: "❓ Câu hỏi", "new-lab": "✨ Lab mới" };

function words(s: string) { return s.trim().split(/\s+/).filter(Boolean).length; }
function shortName(email: string) { return email.split("@")[0]; }
function labIdFromPath(): string | null {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(/^\/lab\/([a-z0-9-]+)\/?$/i);
  if (!m || m[1] === "subject") return null;
  return m[1];
}

export function LabDiscussion() {
  const [labId, setLabId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => { setLabId(labIdFromPath()); }, []);

  const load = useCallback(async () => {
    if (!labId) return;
    setLoading(true);
    try {
      const r = await fetch(`/api/lab/community?lab=${encodeURIComponent(labId)}`, { cache: "no-store" });
      if (r.status === 401) { setNeedLogin(true); setTopics([]); setReplies([]); return; }
      const d = await r.json();
      setNeedLogin(false);
      setTopics(d.topics ?? []);
      setReplies(d.replies ?? []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [labId]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  if (!labId) return null;

  async function vote(t: Topic, dir: 1 | -1) {
    const next = t.myVote === dir ? 0 : dir;
    setTopics((cur) => cur.map((x) => x.id === t.id ? { ...x, myVote: next, score: x.score - x.myVote + next } : x));
    await fetch("/api/lab/community", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ action: "vote", topicId: t.id, value: next }) });
  }

  return (
    <>
      <style>{LD_CSS}</style>
      <button className="ld-fab" onClick={() => setOpen(true)} title="Thảo luận về lab này">
        💬 <span>Thảo luận{topics.length ? ` (${topics.length})` : ""}</span>
      </button>

      {open && (
        <div className="ld-overlay" onClick={() => setOpen(false)}>
          <aside className="ld-drawer" onClick={(e) => e.stopPropagation()}>
            <header className="ld-head">
              <div><b>Thảo luận cộng đồng</b><span>Đóng góp, đề xuất & hỏi đáp cho lab này</span></div>
              <button className="ld-x" onClick={() => setOpen(false)}>×</button>
            </header>

            {needLogin ? (
              <div className="ld-login">
                <p>Bạn cần <b>đăng nhập bằng Google</b> để tạo topic, phản hồi và bình chọn.</p>
                <a className="ld-loginbtn" href={`/login?return_to=${encodeURIComponent(window.location.pathname)}`}>Đăng nhập</a>
              </div>
            ) : (
              <div className="ld-body">
                <NewTopic labId={labId} onDone={load} />
                {loading && !topics.length ? <p className="ld-muted">Đang tải…</p> : null}
                {!loading && !topics.length ? <p className="ld-muted">Chưa có thảo luận nào. Hãy là người đầu tiên đóng góp!</p> : null}
                {topics.map((t) => (
                  <article className="ld-topic" key={t.id}>
                    <div className="ld-vote">
                      <button className={t.myVote === 1 ? "on" : ""} onClick={() => vote(t, 1)} title="Upvote">▲</button>
                      <b>{t.score}</b>
                      <button className={t.myVote === -1 ? "on down" : "down"} onClick={() => vote(t, -1)} title="Downvote">▼</button>
                    </div>
                    <div className="ld-tmain">
                      <div className="ld-tmeta"><span className="ld-kind">{KIND_LABEL[t.kind] ?? t.kind}</span> · {shortName(t.author)}</div>
                      <h4>{t.title}</h4>
                      <p>{t.body}</p>
                      <button className="ld-repbtn" onClick={() => setExpanded(expanded === t.id ? null : t.id)}>
                        {expanded === t.id ? "Ẩn phản hồi" : `Phản hồi (${t.replyCount})`}
                      </button>
                      {expanded === t.id && (
                        <div className="ld-replies">
                          {replies.filter((r) => r.topicId === t.id).map((r) => (
                            <div className="ld-reply" key={r.id}><span>{shortName(r.author)}</span><p>{r.body}</p></div>
                          ))}
                          <ReplyForm topicId={t.id} onDone={load} />
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}

function NewTopic({ labId, onDone }: { labId: string; onDone: () => void }) {
  const [openForm, setOpenForm] = useState(false);
  const [kind, setKind] = useState("suggest");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const wc = useMemo(() => words(body), [body]);
  const fmt = FORMATS.find((f) => f.kind === kind)!;

  async function submit() {
    setErr("");
    if (title.trim().length < 4) { setErr("Tiêu đề quá ngắn."); return; }
    if (wc < MIN_WORDS) { setErr(`Nội dung cần tối thiểu ${MIN_WORDS} từ (hiện ${wc}).`); return; }
    setBusy(true);
    try {
      const r = await fetch("/api/lab/community", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ action: "topic", lab: labId, kind, title: title.trim(), body: body.trim() }) });
      const d = await r.json();
      if (!r.ok) { setErr(d.error ?? "Lỗi."); return; }
      setTitle(""); setBody(""); setOpenForm(false); onDone();
    } finally { setBusy(false); }
  }

  if (!openForm) return <button className="ld-new" onClick={() => setOpenForm(true)}>+ Tạo topic mới</button>;

  return (
    <div className="ld-form">
      <div className="ld-fmts">
        {FORMATS.map((f) => (
          <button key={f.kind} className={f.kind === kind ? "on" : ""} onClick={() => setKind(f.kind)}>{f.label}</button>
        ))}
      </div>
      <input className="ld-input" placeholder="Tiêu đề ngắn gọn…" value={title} onChange={(e) => setTitle(e.target.value)} />
      <textarea className="ld-textarea" rows={5} placeholder={fmt.hint} value={body} onChange={(e) => setBody(e.target.value)} />
      <div className="ld-formfoot">
        <span className={wc >= MIN_WORDS ? "ok" : ""}>{wc}/{MIN_WORDS} từ</span>
        {err && <span className="ld-err">{err}</span>}
        <button className="ld-cancel" onClick={() => setOpenForm(false)}>Hủy</button>
        <button className="ld-submit" disabled={busy || wc < MIN_WORDS} onClick={submit}>{busy ? "Đang gửi…" : "Đăng"}</button>
      </div>
    </div>
  );
}

function ReplyForm({ topicId, onDone }: { topicId: string; onDone: () => void }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const wc = words(body);
  async function submit() {
    setErr("");
    if (wc < MIN_WORDS) { setErr(`Phản hồi cần tối thiểu ${MIN_WORDS} từ (hiện ${wc}).`); return; }
    setBusy(true);
    try {
      const r = await fetch("/api/lab/community", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ action: "reply", topicId, body: body.trim() }) });
      const d = await r.json();
      if (!r.ok) { setErr(d.error ?? "Lỗi."); return; }
      setBody(""); onDone();
    } finally { setBusy(false); }
  }
  return (
    <div className="ld-replyform">
      <textarea rows={3} placeholder="Viết phản hồi (tối thiểu 50 từ)…" value={body} onChange={(e) => setBody(e.target.value)} />
      <div className="ld-formfoot">
        <span className={wc >= MIN_WORDS ? "ok" : ""}>{wc}/{MIN_WORDS} từ</span>
        {err && <span className="ld-err">{err}</span>}
        <button className="ld-submit" disabled={busy || wc < MIN_WORDS} onClick={submit}>{busy ? "…" : "Gửi"}</button>
      </div>
    </div>
  );
}

const LD_CSS = `
.ld-fab{position:fixed;left:16px;bottom:16px;z-index:9990;display:inline-flex;align-items:center;gap:6px;padding:10px 16px;border-radius:999px;border:1px solid #2b3b57;background:rgba(15,26,46,.9);color:#eaf1ff;font-size:13.5px;font-weight:700;cursor:pointer;-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px);box-shadow:0 6px 20px rgba(0,0,0,.35)}
.ld-fab:hover{border-color:#8fd3ff}
.ld-overlay{position:fixed;inset:0;z-index:99990;background:rgba(5,9,16,.55);display:flex;justify-content:flex-end}
.ld-drawer{width:min(520px,100vw);height:100%;overflow-y:auto;background:#0c1524;border-left:1px solid #23324e;color:#eaf1ff;font-family:Arial;animation:ld-in .2s ease}
@keyframes ld-in{from{transform:translateX(30px);opacity:.4}to{transform:translateX(0);opacity:1}}
.ld-head{position:sticky;top:0;display:flex;align-items:center;gap:12px;padding:16px 18px;border-bottom:1px solid #1a2841;background:#0c1524;z-index:1}
.ld-head b{font-size:16px;display:block}
.ld-head span{font-size:12px;color:#93a4c6}
.ld-x{margin-left:auto;width:32px;height:32px;border-radius:50%;border:1px solid #23324e;background:transparent;color:#93a4c6;font-size:18px;cursor:pointer}
.ld-body,.ld-login{padding:16px 18px}
.ld-muted{color:#93a4c6;font-size:14px;text-align:center;padding:18px}
.ld-login p{color:#c7d3e8;line-height:1.6}
.ld-loginbtn{display:inline-block;margin-top:10px;padding:10px 18px;border-radius:10px;background:#8fd3ff;color:#08111f;font-weight:700;text-decoration:none}
.ld-new{width:100%;padding:11px;border-radius:10px;border:1px dashed #2b3b57;background:transparent;color:#8fd3ff;font-weight:700;cursor:pointer;margin-bottom:14px}
.ld-form{border:1px solid #23324e;border-radius:14px;background:#0f1a2e;padding:14px;margin-bottom:16px}
.ld-fmts{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px}
.ld-fmts button{border:1px solid #23324e;background:transparent;color:#93a4c6;padding:6px 10px;border-radius:999px;font-size:12px;font-weight:600;cursor:pointer}
.ld-fmts button.on{border-color:#8fd3ff;color:#8fd3ff;background:rgba(143,211,255,.12)}
.ld-input,.ld-textarea,.ld-replyform textarea{width:100%;box-sizing:border-box;background:#0b1526;border:1px solid #23324e;border-radius:10px;color:#eaf1ff;padding:10px 12px;font-size:14px;font-family:inherit;margin-bottom:8px}
.ld-textarea,.ld-replyform textarea{resize:vertical;line-height:1.5}
.ld-formfoot{display:flex;align-items:center;gap:10px;font-size:12px;color:#93a4c6}
.ld-formfoot .ok{color:#7ee6a8}
.ld-err{color:#ff8f8f}
.ld-cancel{margin-left:auto;background:transparent;border:none;color:#93a4c6;cursor:pointer;font-size:13px}
.ld-submit{background:#8fd3ff;color:#08111f;border:none;border-radius:8px;padding:7px 16px;font-weight:700;cursor:pointer}
.ld-submit:disabled{opacity:.45;cursor:not-allowed}
.ld-topic{display:flex;gap:12px;border-top:1px solid #1a2841;padding:14px 0}
.ld-vote{display:flex;flex-direction:column;align-items:center;gap:2px;min-width:34px}
.ld-vote button{background:transparent;border:none;color:#5b6f8c;font-size:14px;cursor:pointer;line-height:1}
.ld-vote button.on{color:#8fd3ff}
.ld-vote button.on.down{color:#ff8f8f}
.ld-vote b{font-size:13px}
.ld-tmeta{font-size:11.5px;color:#93a4c6;margin-bottom:3px}
.ld-kind{color:#eaf1ff;font-weight:600}
.ld-tmain h4{margin:0 0 5px;font-size:15px;line-height:1.35}
.ld-tmain p{margin:0;font-size:13.5px;color:#c7d3e8;line-height:1.55;white-space:pre-wrap}
.ld-repbtn{margin-top:8px;background:transparent;border:none;color:#8fd3ff;font-size:12.5px;font-weight:600;cursor:pointer;padding:0}
.ld-replies{margin-top:10px;border-left:2px solid #1a2841;padding-left:12px}
.ld-reply{margin-bottom:10px}
.ld-reply span{font-size:11.5px;color:#93a4c6;font-weight:600}
.ld-reply p{margin:2px 0 0;font-size:13px;color:#c7d3e8;line-height:1.5;white-space:pre-wrap}
.ld-replyform{margin-top:8px}
`;
